import {
  classNameKey,
  classNameWithoutDesignator,
  type TSeriesMatchingResponse,
  type TSeriesMatchingRequest,
} from '@schemas'

type Class = TSeriesMatchingResponse['classes'][number]
export type SuggestedMatch = {
  key: string
  name: string
  classes: Class[]
  identityId?: string
  model: string
  subtype: string
  problem?: string
}

/** Only preselect an unambiguous same-name group; broader type-based candidates stay manual. */
export function suggestedMatches(data: TSeriesMatchingResponse): SuggestedMatch[] {
  const groups = new Map<string, Class[]>()
  for (const c of data.classes) {
    if (c.state === 'unmatched') continue
    const key = JSON.stringify([c.division.code, classNameKey(c.effectiveName, c.code)])
    groups.set(key, [...(groups.get(key) ?? []), c])
  }
  return [...groups.entries()].flatMap(([key, classes]) => {
    if (classes.length < 2 || !classes.some(c => c.state === 'unresolved')) return []
    if (new Set(classes.map(c => c.showId)).size !== classes.length) return []
    const identities = [...new Set(classes.flatMap(c => (c.identityId ? [c.identityId] : [])))]
    if (identities.length > 1) return []
    const tuples = new Map(
      classes
        .filter(c => c.catalogClassId)
        .map(c => [JSON.stringify([c.catalogClassId, c.catalogSubtypeId]), c])
    )
    if (tuples.size > 1) return []
    const linked = [...tuples.values()][0]
    const name = classNameWithoutDesignator(classes[0].effectiveName, classes[0].code)
    const existing = identities.length
      ? []
      : data.identities.filter(
          i =>
            !i.retired &&
            Boolean(i.catalogClassId) &&
            i.divisionCode === classes[0].division.code &&
            classNameKey(i.label) === classNameKey(name) &&
            (!linked ||
              (i.catalogClassId === linked.catalogClassId &&
                i.catalogSubtypeId === linked.catalogSubtypeId))
        )
    if (existing.length > 1) return []
    const identity = data.identities.find(i => i.id === identities[0]) ?? existing[0]
    if (identity?.retired) return []
    if (
      identity &&
      linked &&
      (identity.catalogClassId !== linked.catalogClassId ||
        identity.catalogSubtypeId !== linked.catalogSubtypeId)
    )
      return []
    const exactModels = data.models.filter(
      m => !m.retired && classNameKey(m.name) === classNameKey(name)
    )
    const model =
      identity?.catalogClassId ??
      linked?.catalogClassId ??
      (exactModels.length === 1 ? exactModels[0].id : '')
    const subtype = identity?.catalogSubtypeId ?? linked?.catalogSubtypeId ?? ''
    const unchanged = (c: Class) =>
      Boolean(identity && c.identityId === identity.id && c.state === 'assigned')
    const editionOf = (c: Class) => data.editions.find(e => e.showId === c.showId)
    const changed = classes.filter(c => !unchanged(c))
    const problem = changed.some(c => !editionOf(c)?.canEdit)
      ? 'An editor of these years must save this match.'
      : changed.some(c => editionOf(c)?.ownershipRestriction)
        ? 'A year in this match is not run on this installation, so it cannot be saved here.'
        : !identity && !data.canCreateIdentity
          ? 'An administrator must set up this match.'
          : identity && !identity.catalogClassId
            ? 'This saved match has no exhibit type. Review these classes and choose a new match.'
            : undefined
    return [{ key, name, classes, identityId: identity?.id, model, subtype, problem }]
  })
}

export function suggestedMatchRequest(
  data: TSeriesMatchingResponse,
  groups: SuggestedMatch[]
): TSeriesMatchingRequest {
  const input: TSeriesMatchingRequest = {
    showIds: data.editions.map(e => e.showId),
    revision: data.revision,
    operations: [],
    repairs: [],
    newIdentities: [],
    notes: {},
    reason: 'Confirm suggested class matches across years',
  }
  for (const group of groups) {
    const identityId = group.identityId ?? crypto.randomUUID()
    const model = data.models.find(m => m.id === group.model)
    const historicalReview =
      !group.identityId &&
      Boolean(model?.retired || model?.subtypes.some(t => t.id === group.subtype && t.retired))
    if (!group.identityId)
      input.newIdentities.push({
        id: identityId,
        label: group.name,
        catalogClassId: group.model,
        catalogSubtypeId: group.subtype || null,
        divisionCode: group.classes[0].division.code,
      })
    for (const c of group.classes) {
      if (c.state === 'assigned' && c.identityId === identityId) continue
      input.operations.push({
        classId: c.id,
        showId: c.showId,
        state: 'assigned',
        identityId,
        acknowledgeDivisionChange: false,
      })
      input.notes[c.id] = `Confirm same-name match: ${group.name}`
      if (
        historicalReview ||
        c.catalogClassId !== group.model ||
        (c.catalogSubtypeId ?? '') !== group.subtype
      ) {
        input.repairs.push({
          classId: c.id,
          catalogClassId: group.model,
          catalogSubtypeId: group.subtype || null,
          reason: `${historicalReview ? 'Review historical exhibit type' : 'Set exhibit type'} for the confirmed same-name match: ${group.name}`,
        })
      }
    }
  }
  return input
}
